import type { NostrEvent } from '@nostrify/nostrify';

/** Format seconds as `m:ss`. */
export function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Relative time such as "3m ago" or "2d ago". */
export function timeAgo(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** Truncate text to a clean snippet at a word boundary. */
export function snippet(text: string, max = 200): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : max).trimEnd()}…`;
}

/** First tag value for a given tag name. */
export function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([tag]) => tag === name)?.[1];
}

export type GroundCategory = 'somatic' | 'sovereignty' | 'nature' | 'other';

const CATEGORY_META: Record<Exclude<GroundCategory, 'other'>, { label: string; emoji: string }> = {
  somatic: { label: 'Somatic', emoji: '🧘' },
  sovereignty: { label: 'Sovereignty', emoji: '🛡️' },
  nature: { label: 'Nature', emoji: '🌿' },
};

/** Determine which Ground practice a feed event belongs to. */
export function groundCategory(event: NostrEvent): GroundCategory {
  const t = event.tags.filter(([name]) => name === 't').map(([, value]) => value);
  if (t.includes('somatic')) return 'somatic';
  if (t.includes('sovereignty')) return 'sovereignty';
  if (t.includes('nature')) return 'nature';
  return 'other';
}

export function categoryMeta(category: GroundCategory): { label: string; emoji: string } {
  if (category === 'other') return { label: 'Practice', emoji: '🌱' };
  return CATEGORY_META[category];
}

/** Shorten a hex pubkey or npub for display. */
export function shortId(value: string, length = 8): string {
  if (value.length <= length * 2 + 3) return value;
  return `${value.slice(0, length)}…${value.slice(-4)}`;
}

const HEX = '0123456789abcdef';

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) {
    out += HEX[(byte >> 4) & 0xf] + HEX[byte & 0xf];
  }
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.length % 2 === 0 ? hex : `0${hex}`;
  const bytes = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Allow only https URLs from event-sourced metadata before using them as an
 * image `src`. Anything else (javascript:, data:, http:) is rejected.
 */
export function safeImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
}
